import {
  Component,
  For,
  Match,
  Show,
  Switch,
  createEffect,
  createMemo,
  createSignal,
  on,
  onMount,
} from 'solid-js'
import { AppSchema } from '../../../common/types/schema'
import Button from '../../shared/Button'
import Select from '../../shared/Select'
import PersonaAttributes, { fromAttrs, toAttrs } from '../../shared/PersonaAttributes'
import TextInput from '../../shared/TextInput'
import {
  chatStore,
  presetStore,
  responseStore,
  scenarioStore,
  toastStore,
  userStore,
  i18nStore,
} from '../../store'
import { defaultPresets } from '/common/presets'
import { Card, Pill, TitleCard } from '/web/shared/Card'
import { Toggle } from '/web/shared/Toggle'
import TagInput from '/web/shared/TagInput'
import { usePane } from '/web/shared/hooks'
import Divider from '/web/shared/Divider'
import { Check, Minus, Sparkles, Wand, X } from 'lucide-solid'
import { createStore } from 'solid-js/store'
import FileInput, { FileInputResult } from '/web/shared/FileInput'
import { StreamCallback } from '/web/store/data/messages'
import { generateField, MinCharacter } from '../Character/generate-char'
import { RelativeSpinner } from '/web/shared/Loading'
import { isDefaultPreset } from '/common/default-preset'
import { FormLabel } from '/web/shared/FormLabel'
import { useParticipantList } from './util'
import { CharacterDefaultVisibility, VisibilityToggle } from './components/Visibility'
import { neat } from '/common/util'

const formatOptions = (i18n: any) => [
  { value: 'attributes', label: i18n.t.char_attributesFormat },
  { value: 'text', label: i18n.t.char_plainTextFormat },
]

const backupFormats: any = {
  sbf: { value: 'sbf', label: 'SBF' },
  wpp: { value: 'wpp', label: 'W++' },
  boostyle: { value: 'boostyle', label: 'Boostyle' },
}

function genOverrideField(opts: {
  prop: string
  trait?: string
  char: MinCharacter
  tick: StreamCallback
}) {
  if (!opts.char) return

  const min: MinCharacter = {
    name: opts.char.name,
    description: opts.char.description,
    appearance: opts.char.appearance,

    greeting: opts.char.greeting || '',
    persona: opts.char.persona,
    sampleChat: opts.char.sampleChat || '',
    scenario: opts.char.scenario || '',
  }

  generateField({ char: min, prop: opts.prop, trait: opts.trait, tick: opts.tick })
}

const ChatSettings: Component<{
  close: () => void
  footer: (children: any) => void
}> = (props) => {
  const i18n = i18nStore((s) => ({ t: s.t }))
  const state = chatStore((s) => ({ active: s.details[s.lastChatId || ''] }))
  const [generating, setGenerating] = createSignal('')
  const [flags, setFlags] = createSignal<Record<string, boolean | undefined>>({})
  const [edit, setEdit] = createStore(getInitState(state.active?.chat, state.active?.char))

  const user = userStore((s) => ({ user: s.user }))
  const presets = presetStore((s) => ({ list: s.presets }))
  const scenarioState = scenarioStore((s) => ({
    scenarios: s.scenarios,
    loading: s.loading,
    partial: s.partial,
  }))

  const pane = usePane()
  const lists = useParticipantList()

  const saveBackgroundImage = async (files: FileInputResult[]) => {
    if (!files?.length) return
    if (!state.active?.chat) return

    const [file] = files

    chatStore.editChatBackground(file.file)
  }

  const saveLocalSettings = (update: Partial<AppSchema.Chat['localSettings']>) => {
    chatStore.editLocalChatSettings(update)
  }

  const personaFormats = createMemo(() => {
    const format = edit.personaKind
    const base = formatOptions(i18n)
    if (!format || format in backupFormats === false) return base

    return base.concat(backupFormats[format])
  })

  const activePreset = createMemo(() => {
    const presetId = state.active?.chat?.genPreset
    if (!presetId) return

    if (isDefaultPreset(presetId)) return defaultPresets[presetId]
    return presets.list.find((pre) => pre._id === presetId)
  })

  createEffect(
    on(
      () => [state.active?.chat, state.active?.char] as const,
      ([chat, char]) => {
        if (!chat || !char) return
        setFlags(state.active?.chat?.invisible ? { ...state.active?.chat.invisible } : {})
        setEdit(getInitState(chat, char))
      }
    )
  )

  onMount(() => {
    if (scenarioState.scenarios.length) return
    scenarioStore.getAll()
  })

  createEffect(() => {
    setEdit('scenarioId', state.active?.chat?.scenarioIds?.[0] || '')
  })

  createEffect(
    on(
      () => [edit.scenarioId],
      () => {
        const currentText = edit.scenario
        const scenario = scenarioState.scenarios.find((s) => s._id === edit.scenarioId)
        if (
          scenario?.overwriteCharacterScenario &&
          !state.active?.chat?.scenarioIds?.includes(scenario._id)
        ) {
          setEdit('scenario', scenario.text)
        } else {
          setEdit('scenario', currentText)
        }
      }
    )
  )

  const scenarios = createMemo(() => {
    const noScenario = [{ value: '', label: `${i18n.t.chat_none} (${i18n.t.char_scenarioHelper})` }]
    if (scenarioState.loading || scenarioState.partial) {
      return noScenario.concat(
        (state.active?.chat?.scenarioIds ?? []).map((id) => ({
          value: id,
          label: '...',
        }))
      )
    } else {
      return noScenario.concat(
        scenarioState.scenarios.map((s) => ({ label: s.name || i18n.t.admin_notYetImplemented, value: s._id }))
      )
    }
  })

  const genField = (prop: string, trait?: string) => {
    if (generating()) {
      toastStore.warn(`Cannot generator: Already generating`)
      return
    }

    setGenerating(prop)

    const index = trait
      ? edit.personaAttrs.findIndex((a) => a.key === trait)
      : edit.personaAttrs.findIndex((a) => a.key === 'text')

    genOverrideField({
      char: {
        name: edit.name,
        appearance: state.active?.char?.appearance || '',
        description: edit.description || '',

        greeting: edit.greeting,
        persona: {
          kind: edit.personaKind === 'text' ? 'text' : 'attributes',
          attributes: edit.personaAttrs.reduce(
            (prev, curr) => Object.assign(prev, { [curr.key]: [curr.values] }),
            {} as any
          ),
        },
        sampleChat: edit.sampleChat,
        scenario: edit.scenario,
      },
      prop,
      trait,
      tick: (res, st) => {
        if (st === 'done' || st === 'error') {
          setGenerating('')
        }

        if (prop === 'persona') {
          const next = [...edit.personaAttrs]
          next[index] = { key: trait || 'text', values: res }
          setEdit('personaAttrs', next)
          return
        }

        if (prop in edit) {
          setEdit(prop as keyof typeof edit, res)
        }
      },
    })
  }

  const toggle = (charId: string) => {
    const next = { ...flags() }
    const curr = next[charId]
    const flag = curr === undefined ? true : curr === false ? undefined : false
    next[charId] = flag
    setFlags(next)
    chatStore.editChat(state.active?.chat?._id!, { invisible: next }, { quiet: true })
  }

  const onSave = () => {
    const payload = {
      name: edit.name,
      greeting: edit.greeting,
      sampleChat: edit.sampleChat,
      systemPrompt: edit.systemPrompt,
      postHistoryInstructions: edit.postHistoryInstructions,
      scenario: edit.scenario,
      overrides: {
        kind: edit.personaKind,
        attributes: fromAttrs(edit.personaAttrs),
      },
      imageSource: edit.imageSource,
      scenarioIds: edit.scenarioId ? [edit.scenarioId] : [],
      scenarioStates: edit.scenarioStates,
    }
    chatStore.editChat(state.active?.chat?._id!, payload, {
      useOverrides: edit.useOverrides,
      onSuccess: () => {
        if (pane() !== 'pane') {
          props.close()
        }
      },
    })
  }

  const revert = () => {
    const char = state.active?.char
    if (!char) return

    chatStore.editChat(state.active?.chat?._id!, {})
  }

  const Footer = (
    <>
      <div class="flex w-full justify-between gap-2">
        <div>
          <Button schema="secondary" onClick={revert}>
            {i18n.t.chat_settings_reset_character}
          </Button>
        </div>
        <div class="flex gap-2">
          <Button schema="secondary" onClick={props.close}>
            {i18n.t.cancel}
          </Button>
          <Button onClick={onSave}>{i18n.t.update}</Button>
        </div>
      </div>
    </>
  )

  onMount(() => props.footer(Footer))

  return (
    <form class="flex flex-col gap-3">
      <Show when={user.user?.admin}>
        <Card class="text-xs">{state.active?.chat?._id}</Card>
      </Show>

      <Card>
        <Select
          fieldName="imageSource"
          label={i18n.t.chat_settings_image_source}
          helperText={<>{i18n.t.chat_settings_image_source_helper}</>}
          items={[
            { label: i18n.t.chat_settings_main_character, value: 'main-character' },
            { label: i18n.t.chat_settings_last_character, value: 'last-character' },
            { label: i18n.t.chat_settings_chat_settings, value: 'chat' },
            { label: i18n.t.chat_settings_app_settings, value: 'settings' },
          ]}
          value={edit.imageSource}
          onChange={(ev) => setEdit('imageSource', ev.value as any)}
        />
      </Card>

      <Card>
        <div class="flex gap-1">
          <FileInput
            fieldName="chatBackground"
            label={
              <div class="flex items-center justify-between gap-1">
                {i18n.t.chat_settings_background_image}{' '}
                <div class="flex items-center gap-1">
                  <Show when={state.active?.chat?.background}>
                    <Select
                      parentClass="text-xs"
                      items={[
                        { label: i18n.t.settings_ui_backgroundAuto, value: 'auto' },
                        { label: i18n.t.settings_ui_backgroundCover, value: 'cover' },
                        { label: i18n.t.settings_ui_backgroundContain, value: 'contain' },
                      ]}
                      onChange={(next) => saveLocalSettings({ bgFormat: next.value as any })}
                    />
                    <Button size="sm" schema="red" onClick={() => chatStore.removeChatBackground()}>
                      {i18n.t.settings_ui_remove}
                    </Button>
                  </Show>
                </div>
              </div>
            }
            helperText={i18n.t.chat_settings_background_image_helper}
            onUpdate={saveBackgroundImage}
            accept="image/png,image/jpeg,image/apng,image/gif,image/webp"
          />
        </div>
      </Card>

      <Show when={activePreset()?.service !== 'horde'}>
        <Card>
          <Select
            fieldName="mode"
            label={i18n.t.chat_mode}
            helperText={
              <>
                <Show when={state.active?.chat?.mode !== 'companion' && edit.mode === 'companion'}>
                  <TitleCard type="orange">
                    {i18n.t.chat_settings_chat_mode_irreversible}
                  </TitleCard>
                </Show>
              </>
            }
            items={[
              { label: i18n.t.chat_mode_standard, value: 'standard' },
              { label: i18n.t.chat_mode_companion, value: 'companion' },
            ]}
            value={edit.mode}
            onChange={(ev) => setEdit('mode', ev.value as any)}
          />
        </Card>
      </Show>

      <Card>
        <TextInput
          class="text-sm"
          value={edit?.name || ''}
          onChange={(ev) => setEdit('name', ev.currentTarget.value)}
          label={
            <>
              {i18n.t.chat_settings_chat_name}{' '}
              <div
                onClick={() =>
                  responseStore.chatQuery(
                    { question: '', assistant: 'Chat Analyzer' },
                    (msg, state) => {
                      if (state !== 'partial' && state !== 'done') return
                      setEdit('name', msg)
                    }
                  )
                }
              >
                <Wand />
              </div>
            </>
          }
        />
      </Card>

      <Card>
        <Toggle
          value={edit.useOverrides}
          onChange={(ev) => setEdit('useOverrides', ev)}
          label={i18n.t.chat_override_character_definitions}
          helperText={i18n.t.chat_settings_override_helper}
        />
      </Card>

      <Show when={scenarios().length > 1}>
        <Card>
          <Select
            label={i18n.t.char_scenario}
            helperText={i18n.t.chat_scenario_helper}
            items={scenarios()}
            value={edit.scenarioId}
            onChange={(ev) => setEdit('scenarioId', ev.value)}
          />

          <Show when={edit.scenarioId !== ''}>
            <TagInput
              availableTags={[]}
              onSelect={(tags) => setEdit('scenarioStates', tags)}
              label={i18n.t.chat_settings_scenario_state}
              helperText={i18n.t.chat_settings_scenario_state_helper}
              value={edit.scenarioStates}
            />
          </Show>
        </Card>
      </Show>

      <Show when={edit.useOverrides}>
        <Card>
          <TextInput
            class="text-sm"
            isMultiline
            value={edit.description}
            helperText={i18n.t.chat_settings_field_description_helper}
            onChange={(ev) => setEdit('description', ev.currentTarget.value)}
            label={i18n.t.admin_description}
          />

          <TextInput
            class="text-sm"
            isMultiline
            label={
              <GenLabel generating={generating()} label={i18n.t.char_greeting} prop="greeting" gen={genField} />
            }
            value={edit.greeting}
            onChange={(ev) => setEdit('greeting', ev.currentTarget.value)}
          />

          <TextInput
            class="text-sm"
            isMultiline
            value={edit.scenario}
            onChange={(ev) => setEdit('scenario', ev.currentTarget.value)}
            label={
              <GenLabel generating={generating()} label={i18n.t.char_scenario} prop="scenario" gen={genField} />
            }
          />

          <TextInput
            class="text-sm"
            isMultiline
            label={
              <GenLabel
                generating={generating()}
                label={i18n.t.char_sampleConversation}
                prop="sampleChat"
                gen={genField}
              />
            }
            value={edit.sampleChat}
            onChange={(ev) => setEdit('sampleChat', ev.currentTarget.value)}
          />

          <TextInput
            class="text-sm"
            label={i18n.t.char_systemPrompt}
            value={edit.systemPrompt}
            onChange={(ev) => setEdit('systemPrompt', ev.currentTarget.value)}
          />

          <TextInput
            class="text-sm"
            label={i18n.t.chat_settings_post_history_instructions}
            value={edit.postHistoryInstructions}
            onChange={(ev) => setEdit('postHistoryInstructions', ev.currentTarget.value)}
          />

          <Select
            fieldName="schema"
            label={i18n.t.persona}
            items={personaFormats()}
            value={edit.personaKind}
            onChange={(ev) => setEdit('personaKind', ev.value as any)}
          />
          <div class="mt-4 flex flex-col gap-2 text-sm">
            <PersonaAttributes
              state={edit.personaAttrs}
              setter={(next) => setEdit('personaAttrs', next)}
              hideLabel
              schema={edit.personaKind}
              generate={genField}
              disabled={!!generating()}
            />
          </div>
        </Card>
      </Show>

      <Divider />

      <FormLabel
        label={i18n.t.chat_settings_default_visibility}
        helperMarkdown={i18n.t.chat_settings_visibility_helper}
      />

      <div>
        <div class="flex flex-wrap gap-2">
          <Pill small inverse>
            <Check size={16} color="var(--success-500)" class="flex items-center gap-1" />
            {i18n.t.chat_settings_visible}
          </Pill>
          <Pill small inverse>
            <X size={16} color="var(--error-500)" class="flex items-center gap-1" />
            {i18n.t.chat_settings_invisible}
          </Pill>
          <Pill small inverse class="flex items-center gap-1">
            <Minus size={16} />
            {i18n.t.chat_settings_not_set}
          </Pill>
        </div>

        <div>
          <strong>{i18n.t.chat_settings_defaults_all}</strong>
        </div>
        <div class="flex flex-wrap gap-2">
          <For each={lists().chars}>
            {(char) => (
              <VisibilityToggle
                char={char}
                invisible={flags()[char._id]}
                onClick={() => toggle(char._id)}
              />
            )}
          </For>

          <For each={lists().tempsActive}>
            {(char) => (
              <VisibilityToggle
                char={char}
                invisible={flags()[char._id]}
                onClick={() => toggle(char._id)}
              />
            )}
          </For>
        </div>

        <Show when={state.active.chat}>
          <CharacterDefaultVisibility chat={state.active.chat} participants={lists()} />
        </Show>
      </div>
    </form>
  )
}

function getInitState(chat?: AppSchema.Chat, char?: AppSchema.Character) {
  return {
    name: chat?.name || '',
    description: char?.description || '',
    imageSource: chat?.imageSource || 'settings',
    mode: chat?.mode || 'standard',
    useOverrides: !!chat?.overrides,
    scenarioId: chat?.scenarioIds?.[0] || '',
    scenarioStates: chat?.scenarioStates || [],

    greeting: chat?.greeting || char?.greeting || '',
    scenario: chat?.scenario || char?.scenario || '',
    sampleChat: chat?.sampleChat || char?.sampleChat || '',
    systemPrompt: chat?.systemPrompt || '',
    postHistoryInstructions: chat?.postHistoryInstructions || '',

    personaKind: chat?.overrides?.kind || char?.persona.kind || 'text',
    personaAttrs: toAttrs(chat?.overrides?.attributes || char?.persona.attributes),
  }
}

const GenLabel: Component<{
  label: string
  prop: string
  generating: string
  gen: (prop: string, trait?: string) => void
}> = (props) => {
  return (
    <Switch>
      <Match when={props.prop === props.generating}>
        <div class="flex gap-2">
          <Button size="sm" onClick={() => props.gen(props.prop)} disabled>
            <RelativeSpinner size={16} />
          </Button>
          {props.label}
        </div>
      </Match>
      <Match when>
        <div class="flex gap-2">
          <Button size="sm" onClick={() => props.gen(props.prop)} disabled={!!props.generating}>
            <Sparkles size={16} />
          </Button>
          {props.label}
        </div>
      </Match>
    </Switch>
  )
}

export default ChatSettings
